import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import PublicNav from '@/components/public/PublicNav';
import PublicFooter from '@/components/public/PublicFooter';
import { useReveal } from '@/hooks/useReveal';
import { useLang } from '@/i18n/LanguageContext';
import { PageMeta } from '@/components/PageMeta';

const f = "'IBM Plex Mono', monospace";

const inputStyle: React.CSSProperties = {
  fontFamily: f,
  fontSize: 'var(--type-body)',
  width: '100%',
  background: 'transparent',
  border: '0.5px solid var(--lp-border2)',
  borderRadius: 0,
  padding: '0.85rem 1.25rem',
  color: 'var(--lp-ink)',
  outline: 'none',
  marginBottom: 8,
  boxSizing: 'border-box',
};

export default function ContatoPage() {
  const { t } = useLang();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [btnHover, setBtnHover] = useState(false);

  const rTitle = useReveal(100);
  const rHighlight = useReveal(200);
  const rBlock1 = useReveal(200);
  const rBlock2 = useReveal(300);
  const rBlock3 = useReveal(400);
  const rFormLabel = useReveal(150);
  const rForm = useReveal(250);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await (supabase.from('contact_messages' as any).insert({
      name: name.trim(),
      email: email.trim().toLowerCase(),
      subject: subject.trim(),
      message: message.trim(),
    }) as any);
    if (err) {
      setError(t('contato.error'));
    } else {
      setSent(true);
    }
    setLoading(false);
  };

  return (
    <>
      <PublicNav />
      <PageMeta title={t('contato.docTitle') as string} description={t('contato.metaDesc') as string} />
      <main style={{ background: 'var(--lp-bg)', minHeight: '100vh', fontFamily: f, color: 'var(--lp-ink)' }}>
        <section
          className="lp-section"
          style={{ display: 'grid', gridTemplateColumns: '200px 1fr', borderTop: '0.5px solid var(--lp-border)' }}
        >
          <div
            className="lp-section-label split-label"
            style={{ borderRight: '0.5px solid var(--lp-border)', position: 'sticky', top: 72, alignSelf: 'start' }}
          >
            <div style={{ fontSize: 'var(--type-label)', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--lp-muted)' }}>
              <div>002</div>
              <div style={{ marginTop: 4 }}>{t('contato.label')}</div>
            </div>
          </div>
          <div className="split-content">
            <div ref={rTitle.ref} style={rTitle.style}>
              <h1 style={{ fontSize: 'var(--type-h1)', fontWeight: 300, marginBottom: '3rem' }}>{t('contato.title')}</h1>
            </div>

            <div className="contato-inner-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5rem' }}>
              <div>
                <div ref={rHighlight.ref} style={rHighlight.style}>
                  <p style={{
                    fontSize: 'var(--type-h2)', fontWeight: 300, lineHeight: 1.8,
                    color: 'var(--lp-ink)', marginBottom: '3rem', maxWidth: 540, whiteSpace: 'pre-line',
                  }}>
                    {t('contato.highlight')}
                  </p>
                </div>

                {[
                  { label: t('contato.block1Label'), text: t('contato.block1Text'), reveal: rBlock1 },
                  { label: t('contato.block2Label'), text: t('contato.block2Text'), reveal: rBlock2 },
                  { label: t('contato.block3Label'), text: t('contato.block3Text'), reveal: rBlock3 },
                ].map((block) => (
                  <div key={block.label} ref={block.reveal.ref} style={{ ...block.reveal.style, borderTop: '0.5px solid var(--lp-border)', padding: '1.5rem 0' }}>
                    <div style={{ fontSize: 'var(--type-label)', textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--lp-muted)', marginBottom: '0.5rem' }}>
                      {block.label}
                    </div>
                    <p style={{ fontSize: 'var(--type-body)', lineHeight: 1.8, color: 'var(--lp-muted)' }}>
                      {block.text}
                    </p>
                  </div>
                ))}
              </div>

              <div>
                <div ref={rFormLabel.ref} style={rFormLabel.style}>
                  <div style={{
                    fontSize: 'var(--type-label)', textTransform: 'uppercase', letterSpacing: '0.14em',
                    color: 'var(--lp-muted)', marginBottom: '1.5rem',
                  }}>
                    {t('contato.formLabel')}
                  </div>
                </div>
                <div ref={rForm.ref} style={rForm.style}>
                  {sent ? (
                    <div role="status" aria-live="polite">
                      <p style={{ fontSize: 'var(--type-h2)', fontWeight: 300, color: 'var(--lp-ink)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {t('contato.sentTitle')}
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <input type="text" aria-label={t('contato.name')} placeholder={t('contato.name')} value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
                      <input type="email" aria-label={t('contato.email')} placeholder={t('contato.email')} value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
                      <input type="text" aria-label={t('contato.subject')} placeholder={t('contato.subject')} value={subject} onChange={e => setSubject(e.target.value)} style={inputStyle} />
                      <textarea
                        aria-label={t('contato.message')}
                        placeholder={t('contato.message')}
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        required
                        rows={5}
                        style={{ ...inputStyle, resize: 'vertical', fontFamily: f, minHeight: 140 }}
                      />
                      {error && <p role="alert" style={{ fontSize: 'var(--type-label)', color: '#FF3F2D', marginBottom: 8 }}>{error}</p>}
                      <button
                        type="submit"
                        disabled={loading}
                        onMouseEnter={() => setBtnHover(true)}
                        onMouseLeave={() => setBtnHover(false)}
                        style={{
                          fontFamily: f, fontSize: 'var(--type-label)', textTransform: 'uppercase', letterSpacing: '0.14em',
                          width: '100%',
                          background: btnHover ? 'var(--lp-bg)' : 'var(--lp-ink)',
                          color: btnHover ? 'var(--lp-ink)' : 'var(--lp-bg)',
                          border: '0.5px solid var(--lp-ink)',
                          borderRadius: 0, padding: '0.9rem',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.5 : 1,
                          transition: 'background 0.2s ease, color 0.2s ease',
                        }}
                      >
                        {loading ? t('common.sending') : t('common.send')}
                      </button>
                      <p style={{ fontSize: 'var(--type-caption)', color: 'var(--lp-muted)', marginTop: '0.75rem' }}>
                        {t('contato.reply')}
                      </p>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />

      <style>{`
        @media (max-width: 768px) {
          .contato-inner-grid {
            grid-template-columns: 1fr !important;
            gap: 3rem !important;
          }
        }
      `}</style>
    </>
  );
}
